import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { navigate, useLocation } from '@reach/router';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Add from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import EditOutlined from '@mui/icons-material/EditOutlined';
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline';
import PlayArrowOutlined from '@mui/icons-material/PlayArrowOutlined';
import Refresh from '@mui/icons-material/Refresh';
import HistoryOutlined from '@mui/icons-material/HistoryOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import SendOutlined from '@mui/icons-material/SendOutlined';
import { toast } from 'react-toastify';
import ThemedConfirmDialog from '../common/ThemedConfirmDialog';
import AuditHistoryDialog from '../common/AuditHistoryDialog';
import CampaignErrorDialog, { ErrorIconCell } from './CampaignErrorDialog';
import ILocalAGGrid, { getDefaultColDef } from '../grid/iLocalAGGrid';
import {
  CAMPAIGNS_KEY,
  fetchFeatureLocations,
  getFeatureLockedRedirectPath,
} from '../../utils/productLicensing';
import {
  fetchCampaigns,
  fetchCampaignAnalytics,
  fetchCampaignAudit,
  runCampaignAction,
  deleteCampaign,
} from './api';
import CampaignDialog from './CampaignDialog';
import CampaignAnalytics from './CampaignAnalytics';
import { CampaignsHelpGuide, CampaignsHelpButton } from './CampaignsHelpGuide';
import '../message-control-center/message-control-center.css';
import '../message-control-center/template-list.css';
import './campaigns.css';

const LAST_ACTION_KEYS = {
  created: 'Campaigns.ActionCreated',
  updated: 'Campaigns.ActionUpdated',
  scheduled: 'Campaigns.ActionScheduled',
  sent: 'Campaigns.ActionSent',
  paused: 'Campaigns.ActionPaused',
  resumed: 'Campaigns.ActionResumed',
  cancelled: 'Campaigns.ActionCancelled',
  failed: 'Campaigns.ActionFailed',
};

const LAST_ACTION_DEFAULTS = {
  created: 'Created',
  updated: 'Updated',
  scheduled: 'Scheduled',
  sent: 'Sent',
  paused: 'Paused',
  resumed: 'Resumed',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const AUDIENCE_KEYS = {
  one: 'Campaigns.AudienceOne',
  all: 'Campaigns.AudienceAll',
  zip: 'Campaigns.AudienceZip',
};

const STATUS_KEYS = {
  draft: 'Campaigns.StatusDraft',
  scheduled: 'Campaigns.StatusScheduled',
  sending: 'Campaigns.StatusSending',
  paused: 'Campaigns.StatusPaused',
  sent: 'Campaigns.StatusSent',
  failed: 'Campaigns.StatusFailed',
  cancelled: 'Campaigns.StatusCancelled',
};

const SET_FILTER = {
  filter: 'agSetColumnFilter',
  filterParams: {
    buttons: ['reset'],
    defaultToNothingSelected: false,
  },
};

const formatWhen = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const compareUpdatedDay = (filterLocalDateAtMidnight, cellValue) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return -1;
  const cellDay = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate()
  );
  if (cellDay < filterLocalDateAtMidnight) return -1;
  if (cellDay > filterLocalDateAtMidnight) return 1;
  return 0;
};

const sendProgressFor = (row) => {
  const sent = Number((row && row.sent_count) || 0);
  const skipped = Number((row && row.skipped_count) || 0);
  const total = Number((row && row.recipient_count) || 0);
  const status = String((row && row.status) || '');
  const attempted = skipped > 0 || !!(row && row.error) || !!(row && row.sent_at);
  const sendFailed =
    (row && row.send_failed) ||
    status === 'failed' ||
    (row && row.outcome === 'failed') ||
    (sent === 0 && skipped > 0) ||
    (sent === 0 &&
      attempted &&
      status !== 'draft' &&
      status !== 'scheduled');
  const processed = sent + skipped;
  const shown = sendFailed && total > 0 ? Math.max(processed, total) : processed;
  return {
    sendFailed,
    progressLabel: `${shown} / ${total}`,
    displayStatus: sendFailed && status === 'sent' ? 'failed' : status,
  };
};

const StatusCell = (params) => {
  const row = params.data || {};
  const progress = sendProgressFor(row);
  const status = progress.displayStatus || '';
  return (
    <span className={'mcc-campaign-status is-' + status}>
      {progress.sendFailed ? <ErrorOutline fontSize="inherit" /> : null}
      {params.value || '—'}
    </span>
  );
};

const ProgressCell = (params) => {
  const row = params.data || {};
  const progress = sendProgressFor(row);
  const status = String((row && row.status) || '');
  const sent = Number((row && row.sent_count) || 0);
  const skipped = Number((row && row.skipped_count) || 0);
  const total = Number((row && row.recipient_count) || 0);
  const processed = progress.sendFailed
    ? Math.max(sent + skipped, total)
    : sent + skipped;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const sending = status === 'sending';
  const errorText = (row && (row.error_label || row.error)) || '';
  const title =
    errorText ||
    (progress.sendFailed
      ? 'Send failed. None of these messages were delivered.'
      : sending
        ? 'Sending…'
        : '');
  return (
    <div
      className={
        'mcc-campaign-progress-cell' +
        (progress.sendFailed ? ' is-failed' : '') +
        (sending ? ' is-sending' : '')
      }
      title={title}
    >
      {total > 0 || sending ? (
        <LinearProgress
          variant={sending && pct < 100 ? 'indeterminate' : 'determinate'}
          value={pct}
        />
      ) : null}
      <span className="mcc-campaign-progress">
        {progress.sendFailed ? <ErrorOutline fontSize="inherit" /> : null}
        {sending && !progress.sendFailed ? (
          <CircularProgress size={12} thickness={5} />
        ) : null}
        {params.value || progress.progressLabel}
      </span>
    </div>
  );
};

const ErrorCell = ErrorIconCell;

const fallbackActions = (status) => {
  const current = String(status || '')
    .trim()
    .toLowerCase();
  return {
    edit: current !== 'sending' && current !== 'cancelled',
    delete: current !== 'sending',
    pause: current !== 'paused' && current !== 'cancelled' && !!current,
    resume: current === 'paused',
    cancel: current !== 'cancelled' && !!current,
    send: current === 'draft' || current === 'failed' || current === 'sent',
  };
};

const ActionsCell = (params) => {
  const row = params.data;
  if (!row) return null;
  const status = String((row && row.status) || '')
    .trim()
    .toLowerCase();
  const actions = fallbackActions(status);
  const t = params.t;
  const busyRow = params.busyRow || null;
  const pageBusy = !!busyRow;
  const rowBusy = busyRow && busyRow.id === row.id;
  const labelOf = (key, fallback) =>
    (t && t(key, { defaultValue: fallback })) || fallback;
  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const button = (enabled, label, unavailable, onClick, Icon, extraClass, actionKey) => {
    const thisBusy = rowBusy && busyRow.action === actionKey;
    const canClick = enabled && !pageBusy;
    const title = thisBusy
      ? labelOf('Campaigns.Working', 'Working…')
      : enabled
        ? label
        : unavailable || label;
    return (
      <span
        key={label}
        title={title}
        className={
          'mcc-campaign-action-wrap' + (canClick || thisBusy ? '' : ' is-disabled')
        }
      >
        <IconButton
          aria-label={label}
          size="small"
          disabled={!canClick}
          className={extraClass || undefined}
          onClick={(event) => {
            stop(event);
            if (canClick && typeof onClick === 'function') onClick(row);
          }}
        >
          {thisBusy ? (
            <CircularProgress size={16} thickness={5} />
          ) : (
            <Icon fontSize="small" />
          )}
        </IconButton>
      </span>
    );
  };
  return (
    <div className="mcc-template-actions-col mcc-campaign-actions">
      {button(
        actions.edit,
        labelOf('Campaigns.Edit', 'Edit campaign'),
        status === 'cancelled'
          ? labelOf(
              'Campaigns.EditUnavailableCancelled',
              'Cannot edit a cancelled campaign. Delete it, or create a new one.'
            )
          : labelOf(
              'Campaigns.EditUnavailable',
              'Cannot edit while a campaign is sending.'
            ),
        params.onEdit,
        EditOutlined,
        undefined,
        'edit'
      )}
      {button(
        actions.send,
        labelOf('Campaigns.Send', 'Send campaign'),
        status === 'cancelled'
          ? labelOf(
              'Campaigns.SendUnavailableCancelled',
              'Cannot send a cancelled campaign. Delete it, or create a new one.'
            )
          : labelOf(
              'Campaigns.SendUnavailable',
              'Send is available for draft, sent, or failed campaigns.'
            ),
        params.onSend,
        SendOutlined,
        undefined,
        'send'
      )}
      {button(
        actions.pause,
        labelOf('Campaigns.Pause', 'Pause'),
        labelOf(
          'Campaigns.PauseUnavailable',
          'Pause is not available for paused or cancelled campaigns.'
        ),
        params.onPause,
        PauseCircleOutline,
        undefined,
        'pause'
      )}
      {button(
        actions.resume,
        labelOf('Campaigns.Resume', 'Resume'),
        labelOf(
          'Campaigns.ResumeUnavailable',
          'Resume is available for paused campaigns.'
        ),
        params.onResume,
        PlayArrowOutlined,
        undefined,
        'resume'
      )}
      {button(
        actions.cancel,
        labelOf('Campaigns.Cancel', 'Cancel campaign'),
        labelOf(
          'Campaigns.CancelUnavailable',
          'This campaign is already cancelled.'
        ),
        params.onCancel,
        CancelOutlined,
        'mcc-campaign-action-cancel',
        'cancel'
      )}
      {button(
        actions.delete,
        labelOf('Campaigns.Delete', 'Delete'),
        labelOf(
          'Campaigns.DeleteUnavailable',
          'Cannot delete while a campaign is sending.'
        ),
        params.onDelete,
        DeleteOutline,
        undefined,
        'delete'
      )}
      {button(
        true,
        labelOf('Campaigns.AuditHistory', 'Audit history'),
        null,
        params.onHistory,
        HistoryOutlined,
        undefined,
        'history'
      )}
    </div>
  );
};

const CampaignsPage = ({ t }) => {
  const location = useLocation();
  const path = String((location && location.pathname) || '').replace(/\/$/, '');
  const isAnalytics = path.endsWith('/campaigns/analytics');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [busyRow, setBusyRow] = useState(null);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [rules, setRules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpTab, setHelpTab] = useState('guide');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [history, setHistory] = useState(null);
  const [errorTarget, setErrorTarget] = useState(null);

  const openHelp = (tab) => {
    setHelpTab(tab === 'rules' ? 'rules' : 'guide');
    setHelpOpen(true);
  };

  const loadRows = async () => {
    const data = await fetchCampaigns();
    setCampaigns((data && data.campaigns) || []);
    setRules((data && data.rules && data.rules.rules) || []);
  };

  const loadAnalytics = async (days) => {
    const data = await fetchCampaignAnalytics(null, days || analyticsDays);
    setAnalytics(data);
  };

  const refreshRows = async () => {
    const initial = !campaigns.length && !isAnalytics;
    if (initial) setLoading(true);
    else {
      setWorking(
        t('Campaigns.BusyRefresh', { defaultValue: 'Refreshing campaigns…' })
      );
    }
    setError('');
    try {
      await loadRows();
      await loadAnalytics(analyticsDays);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      if (initial) setLoading(false);
      setWorking('');
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchFeatureLocations(),
      fetchCampaigns(),
      fetchCampaignAnalytics(null, 30),
    ])
      .then(([locationData, campaignData, analyticsData]) => {
        if (cancelled) return;
        setLocations((locationData && locationData.locations) || []);
        setCampaigns((campaignData && campaignData.campaigns) || []);
        setRules(
          (campaignData && campaignData.rules && campaignData.rules.rules) || []
        );
        setAnalytics(analyticsData || null);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err && err.status === 403) {
          navigate(getFeatureLockedRedirectPath(CAMPAIGNS_KEY));
          return;
        }
        setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
    setError('');
  };

  const openEdit = (row) => {
    setEditing(row);
    setDialogOpen(true);
    setError('');
  };

  const handleSaved = async (saved, toastKey, defaultValue) => {
    setDialogOpen(false);
    setEditing(null);
    if (toastKey === 'Campaigns.ScheduledToast') {
      toast.info(t(toastKey, { defaultValue }));
    } else if (toastKey === 'Campaigns.FailedToast') {
      toast.error(t(toastKey, { defaultValue }));
    } else {
      toast.success(t(toastKey, { defaultValue }));
    }
    setWorking(
      t('Campaigns.BusyList', { defaultValue: 'Updating campaign list…' })
    );
    try {
      await loadRows();
      await loadAnalytics(analyticsDays);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking('');
    }
  };

  const runAction = async (row, actionName, successKey, defaultValue) => {
    const labels = {
      send: t('Campaigns.BusySend', { defaultValue: 'Sending campaign…' }),
      pause: t('Campaigns.BusyPause', { defaultValue: 'Pausing campaign…' }),
      resume: t('Campaigns.BusyResume', { defaultValue: 'Resuming campaign…' }),
    };
    setBusyRow({ id: row.id, action: actionName });
    setWorking(
      labels[actionName] ||
        t('Campaigns.Working', { defaultValue: 'Working…' })
    );
    try {
      const data = await runCampaignAction(row.id, actionName);
      const campaign = data && data.campaign;
      if (campaign && campaign.status === 'scheduled') {
        toast.info(
          t('Campaigns.ScheduledToast', {
            defaultValue:
              'Promotional messages cannot go out overnight. This campaign is scheduled for 8:00 AM.',
          })
        );
      } else if (
        actionName === 'send' &&
        campaign &&
        (campaign.status === 'failed' || campaign.send_failed)
      ) {
        toast.error(
          t('Campaigns.FailedToast', {
            defaultValue:
              campaign.error || 'Campaign send failed. None were delivered.',
          })
        );
      } else {
        toast.success(t(successKey, { defaultValue }));
      }
      await loadRows();
      await loadAnalytics(analyticsDays);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyRow(null);
      setWorking('');
    }
  };

  const confirmAction = async () => {
    if (!confirmTarget) return;
    setConfirmBusy(true);
    setWorking(
      confirmTarget.action === 'delete'
        ? t('Campaigns.BusyDelete', { defaultValue: 'Deleting…' })
        : t('Campaigns.BusyCancel', { defaultValue: 'Cancelling…' })
    );
    setBusyRow({
      id: confirmTarget.row.id,
      action: confirmTarget.action,
    });
    try {
      if (confirmTarget.action === 'delete') {
        await deleteCampaign(confirmTarget.row.id);
        toast.success(
          t('Campaigns.DeletedToast', { defaultValue: 'Campaign deleted.' })
        );
      } else if (confirmTarget.action === 'cancel') {
        await runCampaignAction(confirmTarget.row.id, 'cancel');
        toast.success(
          t('Campaigns.CancelledToast', {
            defaultValue: 'Campaign cancelled.',
          })
        );
      }
      setConfirmTarget(null);
      await loadRows();
      await loadAnalytics(analyticsDays);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setConfirmBusy(false);
      setBusyRow(null);
      setWorking('');
    }
  };

  const openHistory = async (row) => {
    if (!row) return;
    const title = t('Campaigns.AuditTitle', {
      defaultValue: 'Audit history — {{name}}',
      name: row.name || 'Campaign',
    });
    setHistory({ title, rows: [], loading: true });
    setBusyRow({ id: row.id, action: 'history' });
    try {
      const data = await fetchCampaignAudit(row.id);
      setHistory({
        title,
        rows: (data && data.audit) || [],
        loading: false,
      });
    } catch (err) {
      setHistory(null);
      toast.error(err.message);
    } finally {
      setBusyRow(null);
    }
  };

  const defaultColDef = useMemo(() => getDefaultColDef({ minWidth: 120 }), []);

  const gridRows = useMemo(
    () =>
      (campaigns || []).map((row) => {
        const audienceKey = AUDIENCE_KEYS[row.audience] || 'Campaigns.Audience';
        const audienceLabel = t(audienceKey, {
          defaultValue:
            row.audience === 'all'
              ? 'All patients'
              : row.audience === 'zip'
                ? 'Zip code'
                : 'One patient',
        });
        const progress = sendProgressFor(row);
        const displayStatus = progress.displayStatus || row.status;
        const statusKey = STATUS_KEYS[displayStatus] || 'Campaigns.StatusDraft';
        const lastAction =
          progress.sendFailed && row.last_action === 'sent'
            ? 'failed'
            : row.last_action;
        return {
          ...row,
          status: displayStatus,
          send_failed: progress.sendFailed,
          channel_label:
            row.channel === 'email'
              ? t('Campaigns.Email', { defaultValue: 'Email' })
              : t('Campaigns.Sms', { defaultValue: 'SMS' }),
          audience_label: row.audience_value
            ? `${audienceLabel} · ${row.audience_value}`
            : audienceLabel,
          status_label: t(statusKey, {
            defaultValue: displayStatus === 'failed' ? 'Failed' : row.status,
          }),
          progress_label: progress.progressLabel,
          error_label:
            row.error_label ||
            row.error ||
            (progress.sendFailed
              ? 'The message was not delivered. No detailed provider error was stored for this send.'
              : ''),
          created_by_label: row.created_by_label || '—',
          updated_by_label: row.updated_by_label || '—',
          last_action_label: lastAction
            ? t(LAST_ACTION_KEYS[lastAction] || 'Campaigns.Updated', {
                defaultValue:
                  LAST_ACTION_DEFAULTS[lastAction] || lastAction,
              })
            : '—',
          when_at: row.sent_at || row.scheduled_for || row.updated_at || row.created_on,
        };
      }),
    [campaigns, t]
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'name',
        colId: 'name',
        headerName: t('Campaigns.Name', { defaultValue: 'Campaign name' }),
        minWidth: 180,
      },
      {
        field: 'location_name',
        colId: 'location_name',
        headerName: t('Campaigns.Location', { defaultValue: 'Location' }),
        minWidth: 180,
      },
      {
        field: 'channel_label',
        colId: 'channel_label',
        headerName: t('Campaigns.Channel', { defaultValue: 'Channel' }),
        ...SET_FILTER,
        minWidth: 110,
      },
      {
        field: 'audience_label',
        colId: 'audience_label',
        headerName: t('Campaigns.Audience', { defaultValue: 'Audience' }),
        minWidth: 170,
      },
      {
        field: 'status_label',
        colId: 'status_label',
        headerName: t('Campaigns.Status', { defaultValue: 'Status' }),
        ...SET_FILTER,
        cellRenderer: StatusCell,
        minWidth: 130,
      },
      {
        field: 'progress_label',
        colId: 'progress_label',
        headerName: t('Campaigns.RecipientsCol', {
          defaultValue: 'Sent / recipients',
        }),
        cellRenderer: ProgressCell,
        minWidth: 168,
      },
      {
        field: 'error_label',
        colId: 'error_label',
        headerName: t('Campaigns.Error', { defaultValue: 'Error' }),
        cellRenderer: ErrorCell,
        cellRendererParams: {
          t,
          onViewError: setErrorTarget,
        },
        sortable: false,
        filter: false,
        minWidth: 88,
        maxWidth: 110,
      },
      {
        field: 'created_by_label',
        colId: 'created_by_label',
        headerName: t('Campaigns.CreatedBy', { defaultValue: 'Created by' }),
        minWidth: 150,
      },
      {
        field: 'updated_by_label',
        colId: 'updated_by_label',
        headerName: t('Campaigns.ModifiedBy', { defaultValue: 'Modified by' }),
        minWidth: 150,
      },
      {
        field: 'last_action_label',
        colId: 'last_action_label',
        headerName: t('Campaigns.LastAction', { defaultValue: 'Last action' }),
        ...SET_FILTER,
        minWidth: 130,
      },
      {
        field: 'price_label',
        colId: 'price_label',
        headerName: t('Campaigns.Price', { defaultValue: 'Price' }),
        minWidth: 100,
      },
      {
        field: 'when_at',
        colId: 'when_at',
        headerName: t('Campaigns.Updated', { defaultValue: 'Updated' }),
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: compareUpdatedDay,
          buttons: ['reset'],
          browserDatePicker: true,
        },
        valueFormatter: (params) => formatWhen(params.value),
        minWidth: 180,
      },
      {
        field: 'actions',
        colId: 'actions',
        headerName: t('Campaigns.Actions', { defaultValue: 'Actions' }),
        cellRenderer: ActionsCell,
        cellRendererParams: {
          t,
          onEdit: openEdit,
          onSend: (row) =>
            runAction(
              row,
              'send',
              'Campaigns.SentToast',
              'Campaign sent.'
            ),
          onPause: (row) =>
            runAction(
              row,
              'pause',
              'Campaigns.PausedToast',
              'Campaign paused.'
            ),
          onResume: (row) =>
            runAction(
              row,
              'resume',
              'Campaigns.ResumedToast',
              'Campaign resumed.'
            ),
          onCancel: (row) => setConfirmTarget({ action: 'cancel', row }),
          onDelete: (row) => setConfirmTarget({ action: 'delete', row }),
          onHistory: openHistory,
          busyRow,
        },
        filter: false,
        sortable: false,
        suppressMenu: true,
        minWidth: 304,
        maxWidth: 360,
        flex: 0,
        pinned: 'right',
        cellClass: 'ilocal-ag-actions-col',
      },
    ],
    [t, busyRow]
  );

  const changeAnalyticsDays = async (nextDays) => {
    setAnalyticsDays(nextDays);
    setWorking(
      t('Campaigns.BusyAnalytics', { defaultValue: 'Loading analytics…' })
    );
    try {
      await loadAnalytics(nextDays);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWorking('');
    }
  };

  const pageBusy = loading || !!working;
  const showEmpty = !loading && !campaigns.length && !isAnalytics;
  const showTable = campaigns.length > 0 && !isAnalytics;
  const confirmIsDelete = confirmTarget && confirmTarget.action === 'delete';

  return (
    <div className="mcc-page mcc-campaigns mcc-template-list">
      <div className="mcc-hero">
        <span className="mcc-hero-icon">
          <CampaignIcon />
        </span>
        <div className="mcc-hero-copy">
          <p className="mcc-hero-eyebrow">
            {t('Campaigns.ProductName', { defaultValue: 'Campaigns' })}
          </p>
          <h1>
            {isAnalytics
              ? t('Campaigns.TabAnalytics', { defaultValue: 'Analytics' })
              : t('Campaigns.ListTitle', { defaultValue: 'View campaigns' })}
          </h1>
          <p className="mcc-helper">
            {isAnalytics
              ? t('Campaigns.AnalyticsIntro', {
                  defaultValue:
                    'Delivery and billing for promotional campaigns. You are charged per delivered message, not per campaign.',
                })
              : t('Campaigns.Helper', {
                  defaultValue:
                    'One-off and bulk promotional SMS or email. Uses published Studio templates. Cannot go out overnight.',
                })}
          </p>
        </div>
        <div className="mcc-hero-actions">
          <CampaignsHelpButton t={t} onOpen={openHelp} />
          <Button
            variant="outlined"
            onClick={refreshRows}
            disabled={pageBusy}
            startIcon={
              working && !busyRow ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Refresh />
              )
            }
            sx={{ textTransform: 'none' }}
          >
            {t('Campaigns.Refresh', { defaultValue: 'Refresh' })}
          </Button>
          {isAnalytics ? null : (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openCreate}
              disabled={pageBusy}
              sx={{ textTransform: 'none' }}
            >
              {t('Campaigns.New', { defaultValue: 'New campaign' })}
            </Button>
          )}
        </div>
      </div>

      {pageBusy ? (
        <div className="mcc-inline-progress">
          <LinearProgress />
          <span>
            {working ||
              t('Campaigns.Loading', { defaultValue: 'Loading campaigns…' })}
          </span>
        </div>
      ) : null}
      {error && !dialogOpen ? <p className="mcc-error">{error}</p> : null}

      {showEmpty ? (
        <div className="mcc-template-empty">
          <CampaignIcon />
          <p>
            {t('Campaigns.Empty', {
              defaultValue: 'No campaigns yet. Create one to send promotional SMS or email.',
            })}
          </p>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshRows}
            disabled={pageBusy}
            sx={{ textTransform: 'none', marginRight: '8px' }}
          >
            {t('Campaigns.Refresh', { defaultValue: 'Refresh' })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={openCreate}
            disabled={pageBusy}
            sx={{ textTransform: 'none' }}
          >
            {t('Campaigns.New', { defaultValue: 'New campaign' })}
          </Button>
        </div>
      ) : null}

      {showTable ? (
        <div className="mcc-template-grid-wrap">
          <ILocalAGGrid
            className="ag-theme-alpine mcc-template-grid"
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowModelType="clientSide"
            rowData={gridRows}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 25]}
            rowHeight={56}
            overlayNoRowsMessage={t('Campaigns.NoFilterMatches', {
              defaultValue: 'No campaigns match these filters.',
            })}
            getRowId={(params) => (params.data && params.data.id) || ''}
          />
        </div>
      ) : null}

      {isAnalytics ? (
        <CampaignAnalytics
          t={t}
          analytics={analytics}
          loading={pageBusy}
          days={analyticsDays}
          onDaysChange={changeAnalyticsDays}
          onViewError={setErrorTarget}
        />
      ) : null}

      <CampaignDialog
        open={dialogOpen}
        campaign={editing}
        locations={locations}
        t={t}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />

      <CampaignsHelpGuide
        t={t}
        open={helpOpen}
        tab={helpTab}
        rules={rules}
        onTabChange={setHelpTab}
        onClose={() => setHelpOpen(false)}
      />

      <CampaignErrorDialog
        t={t}
        open={!!errorTarget}
        row={errorTarget}
        onClose={() => setErrorTarget(null)}
      />

      <AuditHistoryDialog
        t={t}
        open={!!history}
        title={history && history.title}
        rows={history && history.rows}
        loading={history && history.loading}
        empty={t('Campaigns.AuditEmpty', {
          defaultValue: 'No audit entries yet for this campaign.',
        })}
        onClose={() => setHistory(null)}
      />

      <ThemedConfirmDialog
        open={!!confirmTarget}
        title={
          confirmIsDelete
            ? t('Campaigns.DeleteTitle', { defaultValue: 'Delete campaign' })
            : t('Campaigns.CancelTitle', { defaultValue: 'Cancel campaign' })
        }
        message={
          confirmTarget
            ? confirmIsDelete
              ? t('Campaigns.DeleteConfirm', {
                  defaultValue:
                    'Delete “{{name}}”? This cannot be undone.',
                  name: confirmTarget.row.name || 'Campaign',
                })
              : t('Campaigns.CancelConfirm', {
                  defaultValue:
                    'Cancel “{{name}}”? Remaining messages will not send.',
                  name: confirmTarget.row.name || 'Campaign',
                })
            : ''
        }
        cancelLabel={
          confirmIsDelete
            ? t('MessageControl.Cancel', { defaultValue: 'Cancel' })
            : t('MessageControl.Close', { defaultValue: 'Close' })
        }
        confirmLabel={
          confirmIsDelete
            ? t('Campaigns.Delete', { defaultValue: 'Delete' })
            : t('Campaigns.Cancel', { defaultValue: 'Cancel campaign' })
        }
        confirmBusyLabel={
          confirmIsDelete
            ? t('Campaigns.BusyDelete', { defaultValue: 'Deleting…' })
            : t('Campaigns.BusyCancel', { defaultValue: 'Cancelling…' })
        }
        busy={confirmBusy}
        onCancel={() => {
          if (!confirmBusy) setConfirmTarget(null);
        }}
        onConfirm={confirmAction}
      />
    </div>
  );
};

export default withTranslation()(CampaignsPage);



// WEBPACK FOOTER //
// ./src/components/campaigns/index.js